import { useEffect, useState } from "react";
import { PackageOpen, Plus, Save, Trash2 } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { MobileShell } from "@/components/MobileShell";
import { PantryBarcodeImportCard } from "@/components/PantryBarcodeImportCard";
import { UpgradePrompt } from "@/components/UpgradePrompt";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "@/components/ui/sonner";
import { useAuth } from "@/context/AuthContext";
import { api, getApiError } from "@/lib/api";
import { FREE_LIMITS, canAddPantryItem, isPremium } from "@/lib/entitlements";


const emptyForm = {
  ingredient_id: "",
  quantity: "",
  unit: "g",
  expiry_date: "",
};


export default function PantryPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [pantry, setPantry] = useState({ items: [], summary: {} });
  const [ingredients, setIngredients] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);

  const premium = isPremium(user);
  const itemCount = pantry.items.length;
  const canAddMore = canAddPantryItem(user, itemCount);
  const showPantryLimitGate = !premium && !canAddMore && !editingId;

  useEffect(() => {
    const loadPageData = async () => {
      const [pantryResponse, ingredientsResponse] = await Promise.all([
        api.get("/pantry"),
        api.get("/ingredients"),
      ]);
      setPantry(pantryResponse.data);
      setIngredients(ingredientsResponse.data);
    };

    loadPageData();
  }, []);

  const selectedIngredient = ingredients.find((ingredient) => ingredient.id === form.ingredient_id);

  const saveItem = async () => {
    try {
      const payload = {
        ingredient_id: form.ingredient_id,
        quantity: Number(form.quantity),
        unit: form.unit,
        expiry_date: form.expiry_date || null,
      };
      const response = editingId
        ? await api.put(`/pantry/items/${editingId}`, payload)
        : await api.post("/pantry/items", payload);
      setPantry(response.data);
      setEditingId(null);
      setForm(emptyForm);
      toast.success(editingId ? "Pantry-item bijgewerkt." : "Pantry-item toegevoegd.");
    } catch (error) {
      if (error?.response?.status === 403 && error?.response?.data?.detail?.error === "premium_required") {
        toast.info(`Smartly Free ondersteunt ${FREE_LIMITS.pantryItems} pantry-items. Upgrade naar Premium voor onbeperkt.`);
        return;
      }
      toast.error(getApiError(error));
    }
  };

  const startEdit = (item) => {
    setEditingId(item.id);
    setForm({
      ingredient_id: item.ingredient_id,
      quantity: String(item.quantity),
      unit: item.unit,
      expiry_date: item.expiry_date || "",
    });
  };

  const removeItem = async (itemId) => {
    try {
      const response = await api.delete(`/pantry/items/${itemId}`);
      setPantry(response.data);
      if (editingId === itemId) {
        setEditingId(null);
        setForm(emptyForm);
      }
      toast.success("Pantry-item verwijderd.");
    } catch (error) {
      toast.error(getApiError(error));
    }
  };

  return (
    <MobileShell title="Pantry" eyebrow="Voorraad eerst opmaken" action={<div className="rounded-full bg-white px-4 py-3 text-xs font-semibold uppercase tracking-[0.24em] text-stone-600" data-testid="pantry-count-pill">{premium ? `${pantry.summary.total_items || 0} items` : `${pantry.summary.total_items || 0} / ${FREE_LIMITS.pantryItems}`}</div>}>
      <div className="space-y-4" data-testid="pantry-page">
        <section className="rounded-[28px] border border-stone-200 bg-[linear-gradient(135deg,_rgba(243,230,198,0.9),_rgba(236,245,234,0.95))] p-4" data-testid="pantry-summary-card">
          <div className="flex items-start gap-3">
            <PackageOpen className="mt-0.5 h-5 w-5 text-emerald-700" />
            <div>
              <p className="text-sm font-semibold text-stone-900" data-testid="pantry-summary-title">
                Je voorraad beïnvloedt direct de boodschappenlijst
              </p>
              <div className="mt-3 flex flex-wrap gap-2 text-xs">
                <span className="rounded-full bg-white/85 px-3 py-1 text-stone-700" data-testid="pantry-total-items-pill">{pantry.summary.total_items || 0} items</span>
                <span className="rounded-full bg-amber-100 px-3 py-1 text-amber-800" data-testid="pantry-expiring-pill">{pantry.summary.expiring_soon_count || 0} bijna op datum</span>
              </div>
              <Button variant="outline" className="mt-4 rounded-full border-stone-200 bg-white" onClick={() => navigate("/grocery")} data-testid="pantry-open-grocery-button">
                Open boodschappenlijst
              </Button>
            </div>
          </div>
        </section>

        <PantryBarcodeImportCard onImported={setPantry} />

        {showPantryLimitGate ? (
          <UpgradePrompt
            reason="pantry-limit"
            title={`Je hebt het Free-limiet van ${FREE_LIMITS.pantryItems} pantry-items bereikt.`}
            description="Upgrade naar Smartly Premium voor een onbeperkte voorraadkast en slimmere boodschappen."
          />
        ) : (
          <section className="rounded-[28px] border border-stone-200 bg-white/90 p-4" data-testid="pantry-form-card">
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="text-xs font-semibold uppercase tracking-[0.24em] text-stone-500">Ingrediënt</label>
              <Select value={form.ingredient_id} onValueChange={(value) => {
                const ingredient = ingredients.find((current) => current.id === value);
                setForm((current) => ({ ...current, ingredient_id: value, unit: ingredient?.default_unit || current.unit }));
              }}>
                <SelectTrigger className="h-12 rounded-2xl border-stone-200" data-testid="pantry-ingredient-select">
                  <SelectValue placeholder="Kies ingrediënt" />
                </SelectTrigger>
                <SelectContent>
                  {ingredients.map((ingredient) => (
                    <SelectItem key={ingredient.id} value={ingredient.id}>
                      {ingredient.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <label className="text-xs font-semibold uppercase tracking-[0.24em] text-stone-500" htmlFor="pantry-quantity">
                  Hoeveelheid
                </label>
                <Input id="pantry-quantity" type="number" min="0" value={form.quantity} onChange={(event) => setForm((current) => ({ ...current, quantity: event.target.value }))} className="h-12 rounded-2xl border-stone-200" data-testid="pantry-quantity-input" />
              </div>
              <div className="space-y-2">
                <label className="text-xs font-semibold uppercase tracking-[0.24em] text-stone-500">Unit</label>
                <Select value={form.unit} onValueChange={(value) => setForm((current) => ({ ...current, unit: value }))}>
                  <SelectTrigger className="h-12 rounded-2xl border-stone-200" data-testid="pantry-unit-select">
                    <SelectValue placeholder="Unit" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="g">g</SelectItem>
                    <SelectItem value="ml">ml</SelectItem>
                    <SelectItem value="pcs">pcs</SelectItem>
                    <SelectItem value="slice">slice</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-xs font-semibold uppercase tracking-[0.24em] text-stone-500" htmlFor="pantry-expiry">
                Houdbaar tot
              </label>
              <Input id="pantry-expiry" type="date" value={form.expiry_date} onChange={(event) => setForm((current) => ({ ...current, expiry_date: event.target.value }))} className="h-12 rounded-2xl border-stone-200" data-testid="pantry-expiry-input" />
            </div>

            {selectedIngredient ? (
              <div className="rounded-[22px] border border-stone-200 bg-stone-50 px-4 py-3 text-sm text-stone-600" data-testid="pantry-selected-ingredient-card">
                {selectedIngredient.name} gebruikt standaard unit <span className="font-semibold text-stone-900">{selectedIngredient.default_unit}</span>
              </div>
            ) : null}

            <div className="grid grid-cols-2 gap-3">
              <Button className="h-12 rounded-full bg-emerald-700 text-white hover:bg-emerald-800" onClick={saveItem} data-testid="pantry-save-button">
                {editingId ? <Save className="mr-2 h-4 w-4" /> : <Plus className="mr-2 h-4 w-4" />}
                {editingId ? "Bijwerken" : "Toevoegen"}
              </Button>
              <Button variant="outline" className="h-12 rounded-full border-stone-200 bg-white" onClick={() => { setEditingId(null); setForm(emptyForm); }} data-testid="pantry-reset-button">
                Reset
              </Button>
            </div>
          </div>
        </section>
        )}

        <section className="space-y-3" data-testid="pantry-items-section">
          {pantry.items.map((item, index) => (
            <div key={item.id} className="stagger-enter rounded-[26px] border border-stone-200 bg-white/90 p-4" style={{ animationDelay: `${index * 55 + 60}ms` }} data-testid={`pantry-item-row-${item.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.28em] text-stone-500" data-testid={`pantry-item-category-${item.id}`}>
                    {item.category}
                  </p>
                  <h2 className="mt-1 text-lg font-semibold text-stone-900" data-testid={`pantry-item-name-${item.id}`}>
                    {item.ingredient_name}
                  </h2>
                  <p className="mt-2 text-sm text-stone-600" data-testid={`pantry-item-quantity-${item.id}`}>
                    {item.quantity} {item.unit}
                  </p>
                  <div className="mt-2 flex flex-wrap gap-2 text-xs">
                    <span className="rounded-full bg-stone-100 px-3 py-1 text-stone-700" data-testid={`pantry-item-location-${item.id}`}>{item.location}</span>
                    <span className="rounded-full bg-stone-100 px-3 py-1 text-stone-700" data-testid={`pantry-item-status-${item.id}`}>{item.status}</span>
                    {item.purchase_unit_label ? <span className="rounded-full bg-emerald-100 px-3 py-1 text-emerald-800" data-testid={`pantry-item-purchase-label-${item.id}`}>{item.purchase_unit_label}</span> : null}
                  </div>
                  <p className="mt-1 text-xs text-stone-500" data-testid={`pantry-item-expiry-${item.id}`}>
                    {item.expiry_date ? `Houdbaar tot ${item.expiry_date}` : "Geen datum ingesteld"}
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" className="rounded-full border-stone-200 bg-white px-3" onClick={() => startEdit(item)} data-testid={`pantry-item-edit-${item.id}`}>
                    Bewerk
                  </Button>
                  <Button variant="outline" className="rounded-full border-red-200 bg-red-50 px-3 text-red-700 hover:bg-red-100" onClick={() => removeItem(item.id)} data-testid={`pantry-item-delete-${item.id}`}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          ))}

          {!pantry.items.length ? (
            <div className="rounded-[26px] border border-dashed border-stone-300 bg-white/70 p-5 text-sm text-stone-600" data-testid="pantry-empty-state">
              Voeg je eerste pantry-item toe om boodschappen automatisch te verminderen.
            </div>
          ) : null}
        </section>
      </div>
    </MobileShell>
  );
}